import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { CheckedState } from '@radix-ui/react-checkbox'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cva, VariantProps } from 'class-variance-authority'
import { type ComponentProps } from 'react'

import { cn } from '@/shared/lib'

import { Icon } from '../icon'

const checkboxVariants = cva(
  [
    'peer m-[3px] shrink-0 cursor-pointer rounded-[2px] border border-gray-300 transition-all outline-none',
    'focus-visible:ring-[3px] focus-visible:ring-gray-200/50',
    'aria-invalid:ring-destructive/20 aria-invalid:border-red-500',
  ],
  {
    variants: {
      color: {
        primary: [
          'data-[state=checked]:border-black data-[state=checked]:bg-black data-[state=checked]:text-white',
        ],
        secondary: [
          'data-[state=checked]:border-purple-500 data-[state=checked]:bg-purple-500 data-[state=checked]:text-white',
          'data-[state=checked]:focus-visible:ring-purple-200/50',
        ],
      },
      size: {
        small: 'size-[14px] [&_svg]:size-3',
        medium: 'size-[18px] [&_svg]:size-4',
      },
      error: {
        true: [
          'border-red-500 ring-red-500/20',
          'data-[state=checked]:border-red-500 data-[state=checked]:bg-red-500 data-[state=checked]:focus-visible:ring-red-500/20',
        ],
      },
      disabled: {
        true: [
          'cursor-default bg-gray-100',
          'data-[state=checked]:border-gray-300 data-[state=checked]:bg-gray-300 data-[state=checked]:text-gray-50',
        ],
      },
    },
  },
)

type Props = Omit<ComponentProps<typeof CheckboxPrimitive.Root>, 'onChange'> &
  VariantProps<typeof checkboxVariants> & {
    onChange?: (checked: CheckedState) => void
  }

export const Root = ({
  className,
  color = 'primary',
  size = 'medium',
  error: errorProp = false,
  disabled: disabledProp = false,
  'aria-invalid': ariaInvalidProp = false,
  onCheckedChange,
  onChange,
  ...restProps
}: Props) => {
  const error = errorProp || Boolean(ariaInvalidProp)
  const disabled = disabledProp || Boolean(restProps['aria-disabled'])

  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        checkboxVariants({ color, size, error, disabled, className }),
      )}
      aria-invalid={error}
      disabled={disabled}
      onCheckedChange={onCheckedChange ?? onChange}
      {...restProps}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        <Icon name="check" strokeWidth={1.5} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

const labelVariants = cva(
  [
    'flex w-fit cursor-pointer gap-1 text-black',
    'has-data-[disabled]:cursor-default',
  ],
  {
    variants: {
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        bold: 'font-bold',
      },
      size: {
        small: 'typo-label1',
        medium: 'typo-body1',
      },
    },
  },
)

type LabelProps = ComponentProps<typeof LabelPrimitive.Root> &
  VariantProps<typeof labelVariants>

export const Label = ({
  className,
  weight = 'normal',
  size = 'medium',
  ...props
}: LabelProps) => {
  return (
    <LabelPrimitive.Root
      className={cn(labelVariants({ weight, size, className }))}
      {...props}
    />
  )
}
